import React, {Component} from 'react'
import {connect} from 'react-redux'
import withRouterLegacy from 'withRouterLegacy'
import {actionCreators} from 'actions'
import api from 'api'
import {recordDisplay, isOfficeAdminSelector} from 'utils'
import moment from 'moment'
import {find, get} from 'lodash'
import StarFlag from 'components/StarFlag'
import styles from 'procedureStatuses/List.scss'
import {DeleteSquare} from 'jbc-front/components/icons'
import Modal from 'jbc-front/components/Modal'
import {notifySuccess} from 'store/actions/notify'
import {asyncError} from 'store/actions/asyncError'
import {useHasBranchOffice} from 'hooks/useHasBranchOffice'
import {getCurrentQueryFromLocation} from './list/utils'
import SearchForm from './list/SearchForm'
import {PaginatorWithPerPage} from './list/PaginatorWithPerPage'

export const procedureTypes = {
  enroll: '入社の手続き',
  resign: '退社の手続き',
  add_dependents: '家族の追加',
  remove_dependents: '家族の削除',
  change_address: '住所の変更',
  change_name: '氏名の変更',
  start_maternity: '産休の開始',
  extend_maternity: '産休の変更',
  finish_maternity: '産休の終了',
  start_childcare: '育休の開始',
  extend_childcare: '育休の延長',
  finish_childcare: '育休の終了',
  additional_childcare_leave_allowance: '2回目以降の育休',
  parental_leave: '出生時育休',
  parental_leave_support_allowance: '出生後休業支援',
  change_office: '転勤の手続き',
  health_insurance_qualification_confirmation: '資格確認書交付',
}

export const statuses = {
  all: 'すべて',
  draft: '途中保存',
  in_progress: '対応中',
  request: '依頼中',
  applying: '承認待ち',
  done: '完了',
}

export const dateNameMap = {
  enroll: '入社予定日',
  resign: '退社予定日',
  change_dependents: '被扶養者異動日',
  change_address: '引越の日',
  change_name: '氏名変更日',
  maternity_leave: '出産予定日',
  childcare_leave: '育休開始日',
  parental_leave: '育休開始日',
  parental_leave_support_allowance: '育休開始日',
  change_office: '転勤年月日',
}

const deleteMessages = {
  draft:
    'この手続きで入力された情報が従業員情報に反映されない場合があります。\n最新の従業員情報は従業員一覧よりご確認ください。',
  in_progress: (
    <>
      この手続きで入力された情報は既に従業員情報に反映されています。
      <br />
      手続き開始前の情報に戻したい場合は、従業員一覧より情報を入力し直してください。
      <br />
      <br />
      <span className={styles.warningText}>
        また、この手続きに関連する電子申請を行っている場合、その電子申請の情報（単独申請、複数名申請のいずれの場合も全ての情報）が
        <br />
        削除されますのでご注意ください。
      </span>
    </>
  ),
  request: '手続きを削除すると、従業員はこの手続きを進めることができなくなります。',
  applying:
    'この手続きで入力された情報はまだ従業員情報に反映されていません。\n情報の反映が必要な場合は一度承認を行ってから削除してください。',
  done: (
    <>
      この手続きで入力された情報は既に従業員情報に反映されています。
      <br />
      手続き開始前の情報に戻したい場合は、再度従業員一覧より情報を変更してください。
      <br />
      <br />
      <span className={styles.warningText}>
        また、この手続きに関連する電子申請を行っている場合、その電子申請の情報（単独申請、複数名申請のいずれの場合も全ての情報）が
        <br />
        削除されますのでご注意ください。
      </span>
    </>
  ),
}

const MAX_FLAG = 4

const Row = ({procedureType, procedureStatus, hasBranchOffice, onRowClick, onStarClick, onDeleteClick}) => {
  const handleDeleteClick = (e, name) => {
    onDeleteClick(name, procedureStatus)
    e.stopPropagation()
  }

  const handleClick = () => {
    onRowClick(procedureStatus.id)
  }
  const handleStarClick = (e) => {
    onStarClick(procedureStatus.id)
    e.stopPropagation()
  }

  const employee = get(procedureStatus, 'employee')

  return (
    <tr onClick={handleClick} className="table-hover">
      <td onClick={handleStarClick}>
        <StarFlag colorIndex={procedureStatus.flag} />
      </td>
      <td>{recordDisplay(procedureStatus.created_at && moment(procedureStatus.created_at).format('YYYY/MM/DD'))}</td>
      <td>{recordDisplay(get(procedureStatus, 'employee.staff_code'))}</td>
      <td>{recordDisplay(employee.display_last_name && employee.display_full_name)}</td>
      {hasBranchOffice && <td>{recordDisplay(employee.office.name)}</td>}
      <td>{recordDisplay(procedureTypes[procedureStatus.procedure_type])}</td>
      <td>{statuses[procedureStatus.status]}</td>
      {procedureType === 'health_insurance_qualification_confirmation' ? null : (
        <td>{recordDisplay(procedureStatus.date_on && procedureStatus.date_on.replace(/-/g, '/'))}</td>
      )}
      <td onClick={(e) => handleDeleteClick(e, employee.display_full_name)} className={styles.delete}>
        <DeleteSquare />
      </td>
    </tr>
  )
}

const ProcedureStatusList = ({procedureType, procedureStatuses, onRowClick, onStarClick, onDeleteClick}) => {
  const hasBranchOffice = useHasBranchOffice()
  return (
    <table className="m-table-list m-table-fixed">
      <thead>
        <tr>
          <th className={styles.flagColumn}>フラグ</th>
          <th className="m-table-list-date">開始日</th>
          <th className="m-table-list-staffcode">スタッフコード</th>
          <th className={styles.nameColumn}>氏名</th>
          {hasBranchOffice && <th className={styles.officeColumn}>適用事業所</th>}
          <th className={styles.procedureColumn}>手続き</th>
          <th className={styles.statusColumn}>ステータス</th>
          {procedureType === 'health_insurance_qualification_confirmation' ? null : (
            <th className="m-table-list-date">{dateNameMap[procedureType] || '関連日'}</th>
          )}
          <th className={styles.deleteColumn}>削除</th>
        </tr>
      </thead>
      <tbody>
        {procedureStatuses.map((procedureStatus) => (
          <Row
            key={procedureStatus.id}
            procedureType={procedureType}
            procedureStatus={procedureStatus}
            hasBranchOffice={hasBranchOffice}
            onRowClick={onRowClick}
            onStarClick={onStarClick}
            onDeleteClick={onDeleteClick}
          />
        ))}
      </tbody>
    </table>
  )
}

class List extends Component {
  constructor(props) {
    super(props)
    this.state = {
      name: null,
      procedureId: null,
      procedureStatus: null,
      procedureType: null,
      showModal: false,
    }
  }

  handleRowClick = (id) => {
    const {procedureStatuses} = this.props
    const procedureStatus = find(procedureStatuses, (procedureStatus) => procedureStatus.id == id)
    if (procedureStatus && procedureStatus.link) {
      this.props.history.push(procedureStatus.link)
    }
  }

  handleStarClick = (id) => {
    const {setFlag, procedureStatuses, token} = this.props
    const procedureStatus = find(procedureStatuses, (procedureStatus) => procedureStatus.id == id)
    if (procedureStatus) {
      setFlag(token, id, (procedureStatus.flag + 1) % MAX_FLAG)
    }
  }

  handleDeleteClick = (name, procedureStatus) => {
    this.setState({
      name: name,
      procedureId: procedureStatus.id,
      procedureStatus: procedureStatus.status,
      procedureType: procedureStatus.procedure_type,
      showModal: true,
    })
  }

  handleModalSubmit = (query) => {
    const {token} = this.props
    this.props.deleteProcedureStatus(token, this.state.procedureId, query)
    this.hideConfirmModal()
  }

  hideConfirmModal = () => {
    this.setState({showModal: false})
  }

  render() {
    const additionalParams = {embed: ['employee', 'request_status', 'office']}
    const {loading, procedureStatuses, user, location, isOfficeAdmin} = this.props
    const query = getCurrentQueryFromLocation(location, user)
    const csvDownloadable =
      procedureStatuses.length > 0 &&
      ['enroll', 'resign', 'change_dependents', 'change_office'].includes(query.procedure_type) &&
      ['all', 'in_progress', 'done'].includes(query.status)

    return (
      <div>
        <div className={styles.headArea}>
          <div className="l-wrap-xxl">
            <h1 className="m-title-main">進行中の手続き一覧</h1>
          </div>
        </div>

        <SearchForm
          additionalParams={additionalParams}
          csvDownloadable={csvDownloadable}
          query={query}
          isOfficeAdmin={isOfficeAdmin}
        />

        <div className="l-wrap-xxl">
          {!loading && procedureStatuses.length === 0 && (
            <p className={styles.searchResultErrorMessage}>手続きが見つかりませんでした</p>
          )}
          <div className="l-overflow-scroll">
            <ProcedureStatusList
              procedureType={query.procedure_type}
              procedureStatuses={procedureStatuses}
              onRowClick={this.handleRowClick}
              onStarClick={this.handleStarClick}
              onDeleteClick={this.handleDeleteClick}
            />
          </div>
          <PaginatorWithPerPage />
        </div>

        <Modal
          header="削除確認"
          body={
            <div>
              <p className="u-mb20">
                {this.state.name}さんの{procedureTypes[this.state.procedureType]}が「
                {statuses[this.state.procedureStatus]}」になっています。
              </p>
              <p className={styles.deleteMessage}>
                「{statuses[this.state.procedureStatus]}」の場合、{deleteMessages[this.state.procedureStatus]}
              </p>
              <p>本当に削除しますか？</p>
            </div>
          }
          cancel="いいえ"
          submit="はい"
          onSubmit={() => this.handleModalSubmit(query)}
          isOpen={this.state.showModal}
          hideModal={() => this.setState({showModal: false})}
        />
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  loading: state.procedureStatuses.list.loading,
  procedureStatuses: state.procedureStatuses.list.data,
  user: state.session.currentUser,
  token: state.auth.token,
  isOfficeAdmin: isOfficeAdminSelector(state),
})

export default withRouterLegacy(
  connect(mapStateToProps, (dispatch) => {
    return {
      loadProcedures(token, query) {
        dispatch(
          actionCreators.procedureStatuses.list.fetchData(
            api.createWithAuth(token).procedureStatuses.list(query, ['employee', 'office'])
          )
        )
      },
      async setFlag(token, id, flag) {
        try {
          dispatch(actionCreators.procedureStatuses.setFlag({id, flag}))
          await api.createWithAuth(token).procedureStatuses.update(id, {flag})
        } catch (err) {
          dispatch(asyncError(err))
        }
      },
      async deleteProcedureStatus(token, id, query) {
        try {
          await api.createWithAuth(token).procedureStatuses.delete(id)
          this.loadProcedures(token, query)
          dispatch(notifySuccess('削除しました'))
        } catch (err) {
          dispatch(asyncError(err))
        }
      },
    }
  })(List)
)
